import { Swiper } from 'swiper';
import { Navigation, Pagination, Thumbs, FreeMode } from 'swiper/modules';
import type { SwiperOptions } from 'swiper/types';

interface MediaItem {
  id: string | number;
  type: string;
  src: string;
  thumb: string;
  alt: string;
}

interface Variant {
  id: string | number;
  featured_media?: {
    id: string | number;
  };
}

interface VariantChangedEvent extends CustomEvent {
  detail: {
    variant: Variant;
    target: HTMLElement & {
      dataset: {
        isColorOption?: string;
      };
    };
  };
}

interface ProductChangedEvent extends CustomEvent {
  detail: {
    media: MediaItem[];
  };
}

interface GallerySelectors {
  containers: {
    swiper: {
      main: string;
      thumbs: string;
    };
    grid: {
      main: string;
    };
  };
  misc: {
    form: string;
  };
}

interface GalleryForm extends HTMLFormElement {
  elements: HTMLFormControlsCollection & {
    id: HTMLInputElement;
  };
}

class ProductGallery extends HTMLElement {
  selectors: GallerySelectors;
  layoutType: string | undefined;
  useVariantMedia: boolean;
  changeGalleryOnColorOnly: boolean;
  form: GalleryForm | null;
  swipers: HTMLElement[];
  grids: HTMLElement[];
  hostToMedia: Map<HTMLElement, HTMLElement[]>;
  private _selectedVariant: Variant | null = null;

  constructor() {
    super();

    this.selectors = {
      containers: {
        swiper: {
          main: "#gallery-swiper-main",
          thumbs: "#gallery-swiper-thumbs",
        },
        grid: {
          main: "#gallery-grid",
        }
      },
      misc: {
        form: `#${this.getAttribute("data-form-id")}`,
      }
    };

    this.layoutType = this.dataset.layout;
    this.useVariantMedia = this.dataset.useVariantMedia === "true";
    this.changeGalleryOnColorOnly = this.dataset.changeGalleryOnColorOnly === "true";
    
    this.form = document.querySelector(this.selectors.misc.form) as GalleryForm | null;
    this.swipers = [];
    this.grids = [];
    this.hostToMedia = new Map();
  }

  connectedCallback(): void {
    this._queryMedia();
    this._initializeSwipers();

    if (this.form) {
      this.form.addEventListener("variant:changed", this._onVariantChanged.bind(this) as EventListener);
    }

    // Listen for product changes (from modal variant swatches)
    this.addEventListener("product:changed", this._onProductChanged.bind(this) as EventListener);

    this.addStickyClass();
    window.addEventListener("resize", this.addStickyClass.bind(this));
  }

  private _queryMedia(): void {
    this.hostToMedia = new Map();

    const selectors = Object.values(this.selectors.containers).flatMap((group) => 
      Object.values(group)
    );

    // Store initial media for further filtering
    selectors.forEach((selector) => {
      const container = this.querySelector(selector) as HTMLElement | null;
      if (!container) {
        return;
      }

      const isSwiper = container.classList.contains("swiper");
      const isThumbsSwiper = container.classList.contains("thumbs-swiper");
      if (isThumbsSwiper) {
        // Push thumbs swipers to the beginning to update them first when filtering
        this.swipers.unshift(container);
      } else if (isSwiper) {
        this.swipers.push(container);
      } else {
        this.grids.push(container);
      }

      this.hostToMedia.set(container, [
        ...container.querySelectorAll("[data-media-id]"),
      ] as HTMLElement[]);
    });
  }

  private _initializeSwipers(): void {
    const thumbsSwiperOptions = (host: HTMLElement): SwiperOptions => ({
      modules: [FreeMode, Thumbs],
      slidesPerView: "auto",
      spaceBetween: parseInt(host.dataset?.spaceBetween || '16', 10),
      freeMode: true,
      watchSlidesProgress: true,
      direction: (host.dataset?.direction as 'horizontal' | 'vertical') || "horizontal",
      breakpoints: JSON.parse(host.dataset?.breakpoints || "{}"),
    });

    const mainSwiperOptions = (host: HTMLElement, thumbsSwiper?: Swiper): SwiperOptions => ({
      modules: [Navigation, Pagination, Thumbs],
      speed: 500,
      loop: true,
      navigation: {
        enabled: true,
        prevEl: this.querySelector(".swiper-button-prev.gallery-nav-button-prev") as HTMLElement,
        nextEl: this.querySelector(".swiper-button-next.gallery-nav-button-next") as HTMLElement,
      },
      pagination: {
        enabled: host.dataset?.enablePagination === "true",
        el: host.querySelector(".swiper-pagination") as HTMLElement,
        clickable: true,
      },
      thumbs: thumbsSwiper ? { swiper: thumbsSwiper } : undefined,
    });

    // Initialize thumbs swipers first
    this.swipers.forEach((host) => {
      if (host.classList.contains("thumbs-swiper")) {
        new Swiper(host, thumbsSwiperOptions(host));
      }
    });

    // Then initialize main swipers with thumbs reference
    this.swipers.forEach((host) => {
      if (host.classList.contains("thumbs-swiper")) {
        return;
      }

      const thumbsSwiperHost = this.swipers.find(
        (swiperHost) => swiperHost.dataset.parentSwiperId === host.id
      );
      const thumbsSwiper = (thumbsSwiperHost as HTMLElement & { swiper?: Swiper })?.swiper;
      new Swiper(host, mainSwiperOptions(host, thumbsSwiper));
    });
    
    this._updateSwipersMedia();
  }

  addStickyClass(): void {
    let indentFromTop = 0;

    const announcementBar = document.querySelector(".announcement-bar") as HTMLElement | null;
    const header = document.querySelector("header") as HTMLElement | null;

    if (announcementBar) {
      indentFromTop += announcementBar.offsetHeight;
    }
    if (header) {
      indentFromTop += header.offsetHeight;
    }

    const stickyElements = document.querySelectorAll("[data-sticky]") as NodeListOf<HTMLElement>;
    stickyElements.forEach((el) => {
      el.style.top = indentFromTop + "px";
    });
  }

  private _onVariantChanged(event: VariantChangedEvent): void {
    this.selectedVariant = event.detail.variant;

    const isColorOption = event.detail.target.dataset.isColorOption === "true";
    if (this.changeGalleryOnColorOnly && !isColorOption) {
      return;
    }
    
    this._updateSwipers();

    if (this.layoutType?.includes("grid")) {
      this._updateGrid();
    }
  }

  /**
   * Handle product change (switching between different products, not variants)
   * Used in modal when selecting different product colors from a collection
   */
  private _onProductChanged(event: ProductChangedEvent): void {
    const { media } = event.detail;
    if (!media?.length) {
      return;
    }

    this._replaceSwipersMedia(media);
  }

  /**
   * Replace all swiper slides with new product media
   */
  private _replaceSwipersMedia(mediaArray: MediaItem[]): void {
    
    // Find swiper hosts
    const thumbsHost = this.swipers.find(host => host.classList.contains("thumbs-swiper")) as HTMLElement & { swiper?: Swiper } | undefined;
    const mainHost = this.swipers.find(host => !host.classList.contains("thumbs-swiper")) as HTMLElement & { swiper?: Swiper } | undefined;

    if (!thumbsHost || !mainHost) {
      console.error('Could not find swiper hosts');
      return;
    }

    // Create slides HTML for both swipers
    const thumbsSlidesHtml = mediaArray.map((media) => `
      <div class="swiper-slide tw-group tw-w-fit !tw-h-fit tw-cursor-pointer">
        <div class="tw-w-[59px] tw-aspect-square tw-relative tw-overflow-hidden">
          <div class="tw-absolute tw-inset-0 tw-z-[1] tw-w-full tw-h-full tw-border-solid tw-border-maastrichtBlue tw-border tw-hidden group-[.swiper-slide-thumb-active]:tw-block"></div>
          <img 
            src="${media.thumb}" 
            alt="${media.alt}"
            class="tw-w-full tw-h-full tw-object-cover tw-rounded-xs tw-box-border tw-transition-opacity tw-duration-700 tw-ease-in-out"
            loading="lazy"
          >
        </div>
      </div>
    `).join('');

    const mainSlidesHtml = mediaArray.map((media, index) => {
      const isFirst = index === 0;
      return `
        <div class="swiper-slide">
          <div
            data-swiper-media-id="${media.id}"
            data-swiper-media-index="${index}"
            class="tw-w-full tw-relative tw-overflow-hidden"
          >
            <img 
              src="${media.src}" 
              alt="${media.alt}"
              class="tw-w-full tw-h-full tw-aspect-square tw-object-cover"
              loading="${isFirst ? 'eager' : 'lazy'}"
            >
          </div>
        </div>
      `;
    }).join('');

    // Destroy existing swipers
    if (thumbsHost.swiper) {
      thumbsHost.swiper.destroy(true, true);
    }
    if (mainHost.swiper) {
      mainHost.swiper.destroy(true, true);
    }

    // Update HTML
    const thumbsWrapper = thumbsHost.querySelector('.swiper-wrapper');
    const mainWrapper = mainHost.querySelector('.swiper-wrapper');
    
    if (thumbsWrapper) thumbsWrapper.innerHTML = thumbsSlidesHtml;
    if (mainWrapper) mainWrapper.innerHTML = mainSlidesHtml;

    // Reinitialize swipers
    const thumbsSwiperOptions: SwiperOptions = {
      modules: [FreeMode, Thumbs],
      slidesPerView: "auto",
      spaceBetween: parseInt(thumbsHost.dataset?.spaceBetween || '8', 10),
      freeMode: true,
      watchSlidesProgress: true,
    };

    const newThumbsSwiper = new Swiper(thumbsHost, thumbsSwiperOptions);

    const mainSwiperOptions: SwiperOptions = {
      modules: [Navigation, Pagination, Thumbs],
      speed: 500,
      loop: true,
      navigation: {
        enabled: true,
        prevEl: this.querySelector(".swiper-button-prev.gallery-nav-button-prev") as HTMLElement,
        nextEl: this.querySelector(".swiper-button-next.gallery-nav-button-next") as HTMLElement,
      },
      pagination: {
        enabled: mainHost.dataset?.enablePagination === "true",
        el: mainHost.querySelector(".swiper-pagination") as HTMLElement,
        clickable: true,
      },
      thumbs: {
        swiper: newThumbsSwiper,
      }
    };

    new Swiper(mainHost, mainSwiperOptions);
  }

  private _updateSwipers(): void {
    if (this.useVariantMedia) {
      this._updateSwipersMedia();
      return;
    }
    this._scrollToFeaturedMedia();
  }

  private _updateGrid(): void {
    if (this.useVariantMedia) {
      this._updateGridMedia();
      return;
    }
    this._highlightFeaturedMedia();
  }

  private _updateSwipersMedia(): void {
    // IMPLEMENTATION NOTE: Swiper still treats hidden children as slides,
    // so we need to manually perform DOM manipulations
    if (!this.useVariantMedia) {
      return;
    }

    this.swipers.forEach((host) => {
      const swiper = (host as HTMLElement & { swiper?: Swiper }).swiper;
      if (!swiper) {
        return;
      }

      const media = this.hostToMedia.get(host);
      media?.forEach((item) => {
        const itemWithDataset = item as HTMLElement & { dataset: { variantId?: string } };
        item.classList.toggle("is-filtered", itemWithDataset.dataset.variantId !== this.selectedVariantId);
      });

      // Collect the variant media
      const variantMedia = media?.filter((item) => !item.classList.contains("is-filtered")) || [];

      // Remove all slides and only append the variant media
      swiper.removeAllSlides();
      swiper.appendSlide(variantMedia);
      swiper.update();
      swiper.slideToLoop(0, 0);
    });
  }

  private _scrollToFeaturedMedia(): void {
    const host = this.swipers.find((host) => host.matches(this.selectors.containers.swiper.main)) as HTMLElement & { swiper?: Swiper } | undefined;
    if (!host) {
      return;
    }

    const featuredMediaId = this.selectedVariant?.featured_media?.id.toString();
    if (!featuredMediaId) {
      return;
    }

    const media = this.hostToMedia.get(host);
    const featuredMediaItem = media?.find((item) => {
      const itemWithDataset = item as HTMLElement & { dataset: { mediaId?: string } };
      return itemWithDataset.dataset.mediaId === featuredMediaId;
    }) as HTMLElement & { dataset: { mediaIndex?: string } } | undefined;
    
    const featuredMediaIndex = featuredMediaItem?.dataset.mediaIndex;
    if (featuredMediaIndex && host.swiper) {
      host.swiper.slideToLoop(parseInt(featuredMediaIndex, 10), 500);
    }
  }

  private _updateGridMedia(): void {
    this.grids.forEach((grid) => {
      this.hostToMedia.get(grid)?.forEach((item) => {
        const itemWithDataset = item as HTMLElement & { dataset: { variantId?: string } };
        item.classList.toggle("is-filtered", itemWithDataset.dataset.variantId !== this.selectedVariantId);
      });
    });
  }

  private _highlightFeaturedMedia(): void {
    if (!this.selectedVariant) {
      return;
    }

    const variantMediaId = this.selectedVariant.featured_media?.id.toString();
    if (!variantMediaId) {
      return;
    }
      
    this.grids.forEach((grid) => {
      this.hostToMedia.get(grid)?.forEach((item) => {
        const itemWithDataset = item as HTMLElement & { dataset: { mediaId?: string } };
        item.classList.toggle("active", itemWithDataset.dataset.mediaId === variantMediaId);
      });
    });
  }

  get selectedVariant(): Variant | null {
    if (!this.form) {
      return null;
    }
    return this._selectedVariant;
  }

  set selectedVariant(variant: Variant | null) {
    if (!variant) {
      return;
    }
    this._selectedVariant = variant;
  }

  get selectedVariantId(): string | null {
    if (!this.form) {
      return null;
    }
    return this.form.elements.id.value;
  }
}

customElements.define('product-gallery', ProductGallery);

